import { cloneDeep } from "lodash";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  subscribeToMoodboardItems,
  deleteMoodboardItem,
  saveMoodboardItem,
} from "./moodboardUtils";

export default function useMoodboardData({ moodboardPath, admin, user }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [moodboardId, setMoodboardId] = useState(null);
  const [createdBy, setCreatedBy] = useState(null);
  const subscribeRef = useRef();

  useEffect(() => {
    if (subscribeRef.current) subscribeRef.current();
    if (moodboardPath) {
      const isAuthenticated = !!user;
      subscribeToMoodboardItems(
        moodboardPath,
        ({ unsubscribe, error, items, id, createdBy: moodboardCreatedBy }) => {
          if (id) {
            setMoodboardId(id);
          }
          if (moodboardCreatedBy !== undefined) {
            setCreatedBy(moodboardCreatedBy);
          }
          if (unsubscribe) {
            subscribeRef.current = unsubscribe;
          }
          if (items) {
            setItems(items);
            setError(null);
          }
          if (error) {
            setError(error);
          }
        },
        isAuthenticated,
      );
    }

    return () => {
      if (subscribeRef.current) subscribeRef.current();
    };
  }, [moodboardPath, user]);

  const deleteItem = useCallback(
    (itemId, callback) => {
      if (moodboardId) {
        let newItems = cloneDeep(items);
        newItems[itemId].deleting = true;
        setItems(newItems);
        deleteMoodboardItem(moodboardId, itemId, ({ error }) => {
          if (error) {
            let errorItems = cloneDeep(items);
            errorItems[itemId].error = error;
            setItems(errorItems);
          } else {
            if (callback) callback({ success: true });
          }
        });
      } else {
        console.error("no id");
      }
    },
    [items, moodboardId],
  );

  const saveItem = useCallback(
    (itemId, item, callback) => {
      if (moodboardId) {
        let newItems = cloneDeep(items);
        if (!newItems) newItems = {};
        newItems[itemId] = item;
        newItems[itemId].uploading = true;
        setItems(newItems);
        saveMoodboardItem(
          moodboardId,
          itemId,
          item,
          ({ progress, error, success }) => {
            if (success) {
              callback({ success });
            }
            if (error) {
              let errorItems = cloneDeep(items);
              if (!errorItems) errorItems = {};
              errorItems[itemId] = item;
              errorItems[itemId].uploading = true;
              errorItems[itemId].error = error;
              setItems(errorItems);
              if (callback) callback({ error });
            }
            if (progress) {
              let progressItems = cloneDeep(items);
              if (!progressItems) progressItems = {};
              progressItems[itemId] = item;
              progressItems[itemId].progress = progress;
              progressItems[itemId].uploading = true;
              setItems(progressItems);
            }
          },
        );
      } else {
        console.error("no moodboard id");
      }
    },
    [items, moodboardId],
  );

  // Allow admin functions if user is the creator OR has an admin email
  const ADMIN_EMAILS = ["luckyalade309@gmail.com", "paulinjeti@gmail.com"];
  const isCreator = user && createdBy && user.uid === createdBy;
  const isAdminEmail = user && user.email && ADMIN_EMAILS.includes(user.email);
  const canEdit = isCreator || isAdminEmail;
  const adminFunctions = canEdit ? { deleteItem, saveItem } : null;

  return {
    items,
    error,
    adminFunctions,
  };
}
